import { Component } from '@angular/core';
import { AngularComponentFactory, QuestionAngular } from 'survey-angular-ui';
import { ElementFactory, Question, Serializer, settings } from 'survey-core';
import { localization } from 'survey-creator-core';

const CUSTOM_QUESTION_TYPE = 'descriptivetext';

// A class that renders a Descriptive Text question
@Component({
  selector: 'app-descriptive-component',
  templateUrl: './descriptive-component.component.html',
  styleUrls: ['./descriptive-component.component.css'],
})
export class DescriptiveComponentComponent extends QuestionAngular<DescriptiveTextModel> {
  getMode() {
    return localStorage.getItem('gptMode');
  }
}

// Register `DescriptiveComponentComponent` as a component that renders the Descriptive Text question type
AngularComponentFactory.Instance.registerComponent(
  CUSTOM_QUESTION_TYPE,
  DescriptiveComponentComponent
);

// A model that extends the Question class and inherits all its properties and methods
export class DescriptiveTextModel extends Question {
  model: {
    caption: string;
    title: String;
    diagnosis_hypothisis: string;
    ICDcode: string;
    score: string;
    prognosis: string;
  } = {
    title: 'question title',
    caption: 'Hope you are doing good? Please share your feedback',
    diagnosis_hypothisis:
      'Curabitur finibus, felis accumsan gravida sodales, nulla leo hendrerit tortor, vel feugiat sapien diam quis ex.',
    prognosis:
      'If the patient is taking medications or supplements that can affect their blood cell counts, adjusting or changing medications may be considered to improve their blood test results.Have you had any procedures or major illnesses.',
    score: '100',
    ICDcode: 'ACVDF56',
  };

  constructor(name) {
    super(name);
    // Create a `LocalizableString` object for the `caption` property
    this.createLocalizableString('caption', this);
    this.createLocalizableString('diagnosis_hypothisis', this);
    this.createLocalizableString('prognosis', this);
    this.createLocalizableString('ICDcode', this);
    this.createLocalizableString('score', this);
  }

  onAnyValueChanged = () => {};

  getType() {
    return CUSTOM_QUESTION_TYPE;
  }
  get textSize() {
    return this.getPropertyValue('textSize');
  }
  set textSize(val) {
    this.setPropertyValue('textSize', val);
  }

  // Returns caption text that corresponds to the current locale
  get caption() {
    return this.getLocalizableStringText('caption');
  }
  // Sets caption text for the current locale
  set caption(val) {
    this.setLocalizableStringText('caption', val);
  }
  // Returns a `LocalizationString` object for the `caption` property
  get locCaption() {
    return this.getLocalizableString('caption');
  }

  // Returns score text that corresponds to the current locale
  get score() {
    return this.getLocalizableStringText('score');
  }
  // Sets score text for the current locale
  set score(val) {
    this.setLocalizableStringText('score', val);
  }
  // Returns a `LocalizationString` object for the `score` property
  get locScore() {
    return this.getLocalizableString('score');
  }

  // Returns diagnosis_hypothisis text that corresponds to the current locale
  get diagnosis_hypothisis() {
    return this.getLocalizableStringText('diagnosis_hypothisis');
  }
  // Sets diagnosis_hypothisis text for the current locale
  set diagnosis_hypothisis(val) {
    this.setLocalizableStringText('diagnosis_hypothisis', val);
  }
  // Returns a `LocalizationString` object for the `diagnosis_hypothisis` property
  get locDiagnosis_hypothisis() {
    return this.getLocalizableString('diagnosis_hypothisis');
  }

  // Returns prognosis text that corresponds to the current locale
  get prognosis() {
    return this.getLocalizableStringText('prognosis');
  }
  // Sets prognosis text for the current locale
  set prognosis(val) {
    this.setLocalizableStringText('prognosis', val);
  }
  // Returns a `LocalizationString` object for the `prognosis` property
  get locPrognosis() {
    return this.getLocalizableString('prognosis');
  }

  // Returns ICDcode text that corresponds to the current locale
  get ICDcode() {
    return this.getLocalizableStringText('ICDcode');
  }
  // Sets ICDcode text for the current locale
  set ICDcode(val) {
    this.setLocalizableStringText('ICDcode', val);
  }
  // Returns a `LocalizationString` object for the `ICDcode` property
  get locICDcode() {
    return this.getLocalizableString('ICDcode');
  }

  get loctitle() {
    return this.getLocalizableString('title');
  }
}

// Register `DescriptiveTextModel` as a constructor for the "descriptivetext" question type
ElementFactory.Instance.registerElement(CUSTOM_QUESTION_TYPE, (name) => {
  return new DescriptiveTextModel(name);
});
// Configure JSON serialization and deserialization rules for the custom properties

Serializer.addClass(
  CUSTOM_QUESTION_TYPE,
  [
    {
      name: 'title',
      category: 'general',
      visibleIndex: 2,
      serializationProperty: 'loctitle',
    },
    {
      name: 'caption',
      category: 'general',
      visibleIndex: 2,
      serializationProperty: 'locCaption',
    },
    {
      name: 'score',
      category: 'general',
      visibleIndex: 2,
      readOnly: true,
      serializationProperty: 'locScore',
      default: '50',
    },
    {
      name: 'diagnosis_hypothisis',
      category: 'general',
      visibleIndex: 2,
      serializationProperty: 'locDiagnosis_hypothisis',
    },
    {
      name: 'ICDcode',
      category: 'general',
      visibleIndex: 2,
      serializationProperty: 'locICDcode',
    },
    {
      name: 'prognosis',
      category: 'general',
      visibleIndex: 2,
      serializationProperty: 'locPrognosis',
    },
    {
      name: 'textSize',
      category: 'general',
      visibleIndex: 3,
      default: 'medium',
      choices: [
        { value: 'small', text: 'Small' },
        { value: 'medium', text: 'Medium' },
        { value: 'large', text: 'Large' },
      ],
    },
  ],
  function () {
    return new DescriptiveTextModel('');
  },
  'question'
);

// Change a placeholder for the Caption property editor
Serializer.findProperty(CUSTOM_QUESTION_TYPE, 'caption').placeholder =
  'Enter descriptive text...';

Serializer.findProperty(CUSTOM_QUESTION_TYPE, 'score').placeholder =
  'Enter score text...';

Serializer.findProperty(CUSTOM_QUESTION_TYPE, 'ICDcode').placeholder =
  'Enter ICDcode text...';

Serializer.findProperty(
  CUSTOM_QUESTION_TYPE,
  'diagnosis_hypothisis'
).placeholder = 'Enter diagnosis_hypothisis text...';

Serializer.findProperty(CUSTOM_QUESTION_TYPE, 'prognosis').placeholder =
  'Enter prognosis text...';

Serializer.findProperty(CUSTOM_QUESTION_TYPE, 'title').placeholder =
  'Enter descriptive text...';

// Change default values for inherited properties
Serializer.getProperty(CUSTOM_QUESTION_TYPE, 'hideNumber').defaultValue = true;
Serializer.getProperty(CUSTOM_QUESTION_TYPE, 'titleLocation').defaultValue =
  'hidden';

// Specify captions to display on the design surface and in Property Grid
const locale = localization.getLocale('');
// locale.qt[CUSTOM_QUESTION_TYPE] = "Algocyte Question";
locale.pe.caption = 'Caption text';
locale.pe.sore = 'Score text';
locale.pe.diagnosis_hypothisis = 'diagnosis_hypothisis text';
locale.pe.prognosis = 'prognosis text';
locale.pe.ICDcode = 'ICDcode text';
locale.pe.title = 'title text';
locale.pe.textSize = 'Text size';

// Use the Text icon for the Descriptive Text question type
settings.customIcons['icon-descriptivetext'] = 'icon-text';
